import { z } from 'zod';

import { apiClient } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { tokenStore } from '@/lib/api/token-store';

import { userSchema } from './schemas';
import type {
  AuthSuccess,
  LoginPasswordRequest,
  NewPasswordForm,
  OtpDispatch,
  SendOtpRequest,
  User,
} from './types';

/** ADR-0029 — parsed separately from `userSchema`, which stays flat/role-free per the plan's
 *  "no role union" decision; these two fields describe the *session*, not the Grower profile. */
const sessionExtrasSchema = z.object({
  accountSelectionRequired: z.boolean().catch(false),
  // Confirmed live 2026-10-14: plain account-type codes, not `{accountType, label}` objects.
  availableAccounts: z.array(z.string()).catch([]),
  activeAccount: z
    .string()
    .nullish()
    .transform((v) => v ?? null),
});

/**
 * Take the session the server just issued.
 *
 * The `refreshToken` is captured, not discarded. `POST /auth/tracking/refresh` takes it in the
 * **body** — it is not the HttpOnly cookie ADR-0005 assumed — so login is the only place the
 * client ever receives one. Dropping it here is what made every refresh answer
 * `400 VALIDATION_FAILED` and every session die at access-token expiry.
 *
 * It is optional on the type because the reset-password path adopts a session without one.
 */
function adoptSession(payload: {
  accessToken: string;
  refreshToken?: string;
  user: unknown;
  accountSelectionRequired?: unknown;
  availableAccounts?: unknown;
  activeAccount?: unknown;
}): AuthSuccess {
  const { accessToken, refreshToken, user } = payload;
  const parsed = userSchema.parse(user);
  const extras = sessionExtrasSchema.parse({
    accountSelectionRequired: payload.accountSelectionRequired,
    availableAccounts: payload.availableAccounts,
    activeAccount: payload.activeAccount,
  });
  tokenStore.setAccessToken(accessToken);
  if (refreshToken) tokenStore.setRefreshToken(refreshToken);
  return { accessToken, user: parsed, ...extras };
}

/** The raw session reply shape every `adoptSession` caller's `apiClient.post` is typed as — ADR-0029's
 *  three fields are confirmed live (DevTools capture, 2026-10-14) on login/otp-verify; `switchAccount`
 *  is expected to answer the same shape once its endpoint is confirmed. */
type RawSessionReply = {
  accessToken: string;
  refreshToken?: string;
  user: unknown;
  accountSelectionRequired?: unknown;
  availableAccounts?: unknown;
  activeAccount?: unknown;
};

export async function loginWithPassword(body: LoginPasswordRequest): Promise<AuthSuccess> {
  return adoptSession(await apiClient.post<RawSessionReply>(endpoints.auth.login, body));
}

export async function sendLoginOtp(body: SendOtpRequest): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpSend, body);
}

export async function resendLoginOtp(body: SendOtpRequest): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.otpResend, body);
}

export async function verifyLoginOtp(body: {
  referenceId: string;
  otp: string;
}): Promise<AuthSuccess> {
  return adoptSession(await apiClient.post<RawSessionReply>(endpoints.auth.otpVerify, body));
}

export async function sendForgotOtp(phone: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotSend, { phone });
}

export async function resendForgotOtp(phone: string): Promise<OtpDispatch> {
  return apiClient.post<OtpDispatch>(endpoints.auth.forgotResend, { phone });
}

export async function verifyForgotOtp(body: { referenceId: string; otp: string }): Promise<string> {
  const { passwordResetToken } = await apiClient.post<{ passwordResetToken: string }>(
    endpoints.auth.forgotVerify,
    body,
  );
  tokenStore.setResetToken(passwordResetToken);
  return passwordResetToken;
}

/**
 * Sends **both** halves of the pair. The endpoint validates the match server-side and rejects a
 * body carrying `newPassword` alone with `400 VALIDATION_FAILED` / `confirmPassword: "Passwords do
 * not match"` — the client-side `newPasswordSchema` refine is a courtesy, not the authority. Same
 * payload Tracking Mobile sends. (Profile change-password is the opposite: `newPassword` alone.)
 */
export async function resetPassword(body: NewPasswordForm): Promise<void> {
  const token = tokenStore.getResetToken();
  await apiClient.post(endpoints.auth.forgotReset, body, {
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
}

/**
 * ADR-0029 — mints a session for a linked account with no re-auth, the same reply shape login
 * returns, so it is adopted the same way. Two callers: the post-login **Select Account** screen
 * passes `accountType` explicitly (more than one option on screen); the mid-session **Switch
 * Profile** menu row omits it — there is only one "other" account to mean.
 *
 * Payload key is `account`, not `accountType` — matches greenway_mobile's `SignInService
 * .selectAccount`, which sends `{ "account": "grower" | "associate_grower" }` and is confirmed
 * against a real Postman stub collection (2026-10-08). The earlier `{ accountType }` guess here
 * never matched any contract and is why account switching 404'd/validation-failed in this app.
 */
export async function switchAccount(accountType?: string): Promise<AuthSuccess> {
  return adoptSession(
    await apiClient.post<RawSessionReply>(
      endpoints.auth.switchAccount,
      accountType ? { account: accountType } : undefined,
    ),
  );
}

export async function fetchCurrentUser(): Promise<User> {
  return userSchema.parse(await apiClient.get(endpoints.auth.me));
}

export async function logout(): Promise<void> {
  await apiClient.post(endpoints.auth.logout);
  tokenStore.clear();
}

/**
 * Restore the session on boot — the call `<AuthProvider>` makes before deciding `authenticated`
 * vs `unauthenticated`.
 *
 * Sends the stored refresh token and adopts the rotated pair, exactly as the interceptor's own
 * refresh does. It previously posted **no body**, which the API answers
 * `400 VALIDATION_FAILED { field: 'refreshToken' }`, so this always failed and every page load
 * landed on the login screen. Throwing when there is nothing to send keeps the boot honest: no
 * token means no session to restore, and spending a request to be told so is noise on an endpoint
 * that is rate limited.
 */
export async function refreshSession(): Promise<User> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  const session = await apiClient.post<{ accessToken?: string; refreshToken?: string }>(
    endpoints.auth.refresh,
    { refreshToken },
  );
  if (!session?.accessToken) throw new Error('Refresh returned no access token');

  tokenStore.setAccessToken(session.accessToken);
  // Rotation is the server's choice; keep the token just sent if it answered without a new one.
  if (session.refreshToken) tokenStore.setRefreshToken(session.refreshToken);

  return fetchCurrentUser();
}
